import { useState, useEffect } from "react";
import api from "@/lib/api";
import { TeamBadge } from "@/components/TeamBadge";
import { useI18n } from "@/context/I18nContext";
import { ArrowRightLeft, ArrowUpRight, ArrowDownRight, Star, Radio } from "lucide-react";

const GROUPS = ["GK", "DEF", "MID", "FWD"];
const TAGS = ["star", "key", "watch", "injured"];

function StarRating({ value, onRate, pkey }) {
  return (
    <div className="flex gap-0.5" data-testid={`stars-${pkey}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} onClick={() => onRate(value === i ? 0 : i)} data-testid={`star-${pkey}-${i}`}
          className="transition-transform hover:scale-110" title={`${i}`}>
          <Star size={14} className={i <= value ? "fill-primary text-primary" : "text-muted-foreground"} />
        </button>
      ))}
    </div>
  );
}

export function SquadsView({ leagueId }) {
  const { t } = useI18n();
  const [teams, setTeams] = useState([]);
  const [selected, setSelected] = useState(null);
  const [squad, setSquad] = useState(null);
  const [ratings, setRatings] = useState({});

  useEffect(() => {
    setSquad(null); setSelected(null);
    api.get(`/leagues/${leagueId}/teams`).then(({ data }) => {
      setTeams(data.teams);
      if (data.teams.length) selectTeam(data.teams[0].id);
    });
  }, [leagueId]);

  const selectTeam = async (id) => {
    setSelected(id);
    setSquad(null);
    const [sq, rt] = await Promise.all([
      api.get(`/teams/${id}/squad`),
      api.get(`/teams/${id}/ratings`),
    ]);
    setSquad(sq.data);
    setRatings(rt.data.ratings || {});
  };

  const saveRating = async (pkey, next) => {
    setRatings((s) => ({ ...s, [pkey]: next }));
    await api.put(`/teams/${selected}/players/${encodeURIComponent(pkey)}/rating`, next);
  };

  const rate = (pkey, rating) => {
    const cur = ratings[pkey] || { rating: 0, tag: null };
    saveRating(pkey, { rating, tag: cur.tag });
  };
  const tagPlayer = (pkey, tag) => {
    const cur = ratings[pkey] || { rating: 0, tag: null };
    saveRating(pkey, { rating: cur.rating, tag: tag || null });
  };

  const isLive = squad && !squad.is_sample;
  const ages = squad ? squad.players.map((p) => p.age).filter((a) => a) : [];
  const avgAge = ages.length ? Math.round((ages.reduce((s, a) => s + a, 0) / ages.length) * 10) / 10 : null;

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="squad-team-picker">
        {teams.map((tm) => (
          <button key={tm.id} onClick={() => selectTeam(tm.id)} data-testid={`squad-team-btn-${tm.short}`}
            className={`flex items-center gap-2 px-3 py-1.5 border text-sm transition-colors ${
              selected === tm.id ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"
            }`}>
            <TeamBadge team={tm} size={18} />
            <span className="hidden sm:inline">{tm.short}</span>
          </button>
        ))}
      </div>
      <p className="text-muted-foreground text-xs mb-5">{t("rating.hint")}</p>

      {squad && (
        <div data-testid="squad-panel">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <TeamBadge team={squad.team} size={30} />
              <h3 className="font-display text-2xl">{squad.team.name}</h3>
            </div>
            {isLive ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[hsl(var(--pitch-green))] border border-[hsl(var(--pitch-green))]/40 px-1.5 py-0.5" data-testid="squad-live-badge">
                <Radio size={10} /> Live roster
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5 py-0.5" data-testid="squad-sample-badge">{t("archive.sample")}</span>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-5">
              {GROUPS.map((g) => {
                const group = squad.players.filter((p) => p.position === g);
                if (!group.length) return null;
                return (
                <div key={g}>
                  <p className="overline text-primary mb-2">{t(`pos.${g}`)}</p>
                  <div className="bg-card border border-border">
                    {group.map((p) => {
                      const r = ratings[p.key] || { rating: 0, tag: null };
                      return (
                        <div key={p.key} className="flex items-center gap-3 px-4 py-2.5 border-t border-border first:border-t-0" data-testid={`squad-player-${p.key}`}>
                          <span className="font-display text-lg w-7 text-center text-muted-foreground">{p.number ?? "·"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium truncate">{p.name}</span>
                              {(p.nationality || p.age) && (
                                <span className="text-[11px] text-muted-foreground font-data">
                                  {p.nationality}{p.nationality && p.age ? " · " : ""}{p.age ? `${p.age}y` : ""}
                                </span>
                              )}
                              {p.is_new && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-primary/15 text-primary px-1.5 py-0.5" data-testid={`transfer-in-${p.key}`}>
                                  <ArrowUpRight size={10} /> {t("squad.in")} · {t("squad.from")} {p.joined_from}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <StarRating value={r.rating} pkey={p.key} onRate={(x) => rate(p.key, x)} />
                              <select value={r.tag || ""} onChange={(e) => tagPlayer(p.key, e.target.value)}
                                data-testid={`tag-select-${p.key}`}
                                className="bg-secondary border border-border text-xs px-2 py-1 focus:border-primary focus:outline-none">
                                <option value="">{t("tag.none")}</option>
                                {TAGS.map((tg) => <option key={tg} value={tg}>{t(`tag.${tg}`)}</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>

            <div>
              {isLive ? (
                <div data-testid="squad-info">
                  <div className="flex items-center gap-2 mb-2">
                    <Radio size={14} className="text-[hsl(var(--pitch-green))]" />
                    <p className="overline text-muted-foreground">Squad · {squad.team.name}</p>
                  </div>
                  <div className="bg-card border border-border divide-y divide-border">
                    <div className="flex items-center justify-between px-4 py-3">
                      <span className="text-sm text-muted-foreground">Players in squad</span>
                      <span className="font-display text-xl">{squad.players.length}</span>
                    </div>
                    {avgAge && (
                      <div className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-muted-foreground">Average age</span>
                        <span className="font-display text-xl">{avgAge}</span>
                      </div>
                    )}
                    {GROUPS.map((g) => {
                      const c = squad.players.filter((p) => p.position === g).length;
                      return (
                        <div key={g} className="flex items-center justify-between px-4 py-2">
                          <span className="text-xs uppercase tracking-wider text-muted-foreground">{t(`pos.${g}`)}</span>
                          <span className="font-data text-sm">{c}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRightLeft size={14} className="text-muted-foreground" />
                    <p className="overline text-muted-foreground">{t("squad.transfersOut")} · {squad.window}</p>
                  </div>
                  <div className="bg-card border border-border" data-testid="transfers-out">
                    {(squad.transfers_out || []).map((p, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-border first:border-t-0" data-testid={`transfer-out-${i}`}>
                        <ArrowDownRight size={14} className="text-destructive" />
                        <span className="font-medium flex-1">{p.name}</span>
                        <span className="text-xs text-muted-foreground">{p.position}</span>
                        <span className="text-xs">{t("squad.to")} <span className="text-foreground font-medium">{p.to_club}</span></span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
